import type { ImportDetail } from "@nu-assist/contracts";
/**
 * The Search block — the linked Thrive search plus the two actions that write
 * into it.
 *
 * Was one of four cards in `ImportInfoGrid`; on 2026-08-26 the other three went
 * away (Date and Contact list title deleted, Contacts folded into the stats
 * popover) and this one moved up beside the title. It is the only block on the
 * page a recruiter ACTS from, so it earns the top-right corner rather than a
 * slot in a grid of read-only facts.
 *
 * **No bounding box, and the actions come FIRST** (2026-08-26). The tinted card
 * around it was doing two jobs badly: it added chrome to the tallest block in
 * the header, and it spent the type colour on a block that is the same on every
 * type. The colour moved to `CriteriaBox`, where it actually distinguishes
 * something. What is left is two lines with nothing drawn around them — the
 * actions on top because they are why a recruiter looks here, the search they
 * write into underneath as their subject.
 *
 * No `Search:` label either: the value is an id-and-name pair linked to Thrive,
 * sitting directly under two buttons that both name Thrive. Nothing else it
 * could be.
 *
 * The two actions are two ordinary `@nu-assist/ui` buttons with an `ActionArrow`
 * between them (2026-08-27). They were a chevron step-flow — cut with
 * `clip-path` so the first pointed into the second — until the shape's cost was
 * named: a clipped element cannot carry a border, an elevation or an outset
 * focus ring, so neither control could look pressable. `ActionArrow` carries
 * the reasoning; the short version is that the order hint moved OUT of the
 * controls so the controls could be controls.
 *
 * The "Creates contacts in Thrive but does not add to a search" caveat used to
 * be a third line of prose under the buttons. It is an ⓘ now: it explains ONE
 * button, and a permanent line of explanation next to a button the recruiter
 * presses weekly is a line they stop reading by the third time.
 *
 * **It is NOT only an ⓘ, and that is deliberate.** `/pr-review` iter 1 on the
 * cl.4 work (Agent A 3(c)+3(d), Agent C M4) rejected exactly this move once,
 * for three reasons worth keeping: a `Tooltip` wrapper adds a tab stop before
 * the page's primary action; it carries `aria-describedby` on its own span, so
 * a screen reader focused on the BUTTON never hears what the button does; and
 * hover-only puts the one sentence explaining the action out of reach of
 * keyboard and touch entirely.
 *
 * So the note has TWO surfaces: a visually-hidden paragraph that the button
 * points at with `aria-describedby` (assistive tech keeps hearing it, exactly
 * as before), and the ⓘ for sighted readers, itself focusable so keyboard
 * users can open it. What was removed is the permanent visual line — not the
 * information, and not its accessibility.
 */

import { Target } from "lucide-react";
import { Link } from "react-router-dom";

import { ImportSearchLinks } from "../../../features/imports/selection/ImportSearchLinks";
import { InfoHint } from "./InfoHint";

export function SearchCard({
  detail,
  actions,
  actionNote,
  actionNoteId,
  bullseyeHref,
}: {
  detail: ImportDetail;
  /** The Compare-to-Thrive / Add-to-search pair. */
  actions?: React.ReactNode;
  /** The caveat for the write action: behind an ⓘ visually, sr-only for AT. */
  actionNote?: React.ReactNode;
  /** Id the write action's `aria-describedby` points at. */
  actionNoteId?: string;
  /**
   * The search's Bullseye with only this AI Review on, for an AI Review list
   * applied to a linked search; absent everywhere else. An icon after the
   * search links, in-tab like the search name beside it.
   */
  bullseyeHref?: string | null;
}) {
  return (
    // The ⓘ sits OUTSIDE the column that the buttons and the search link share.
    // Inside it, it widened the column by its own width and the right-aligned
    // link then lined up with the ⓘ's edge instead of the buttons' — visibly
    // overhanging them. Now the inner column is exactly as wide as the actions,
    // so `items-end` aligns the two things that should agree, and the hint
    // hangs off the end of the pair it annotates.
    <div data-testid="search-card" className="flex items-start justify-end gap-1.5">
      <div className="flex min-w-0 flex-col items-end gap-1">
        {actions ? (
          // `gap-2` and `items-center`: the arrow is a 12px-tall triangle
          // between two 34px buttons, so it only reads as pointing FROM one
          // INTO the other when it sits on their shared centre line.
          <div data-testid="search-actions" className="flex items-center gap-2">
            {actions}
          </div>
        ) : null}
        {/* `text-caption`, matching the type tails opposite — same header row,
            same dead `text-body-sm` before it (round 9, Agent A §2.3). */}
        <div className="flex flex-wrap items-baseline justify-end gap-x-2 text-caption text-text">
          {/* The shared `ImportSearchLinks` — Import Detail is where the
              recruiter lands FROM the listing row, so the two are seen back to
              back and a third spelling of the id/name pair would be the most
              visible inconsistency of all. */}
          <ImportSearchLinks
            searchIdThrive={detail.search_id_thrive ?? null}
            searchName={detail.search_name ?? null}
            className="break-all"
            testIdPrefix="import-detail-search"
            newTab={false}
          />
          {bullseyeHref ? (
            <Link
              to={bullseyeHref}
              title="Open this AI Review in Bullseye"
              aria-label="Open this AI Review in Bullseye"
              data-testid="import-detail-bullseye-link"
              className="inline-flex items-center self-center rounded-sm text-accent hover:text-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Target aria-hidden="true" className="h-4 w-4" />
            </Link>
          ) : null}
        </div>
      </div>
      {actionNote ? (
        <>
          {/* The AT surface. `sr-only`, so there is no visual line but the
              button's `aria-describedby` still resolves to real text in the
              accessibility tree — see `InfoHint` for the review round that
              makes this non-optional. */}
          <p id={actionNoteId} data-testid="add-action-note" className="sr-only">
            {actionNote}
          </p>
          <InfoHint content={actionNote} label="About this action" />
        </>
      ) : null}
    </div>
  );
}
